import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { WireActivityItem } from '@logistics/contracts';
import { api } from '../client';
import { expectStatus } from '@/lib/result';
import type { ActivityType } from './activity-types';

import { PAGE_SIZE } from './activity-query';
import type { ActivityView } from './activity-query';

type ActivityPage = { items: WireActivityItem[]; total?: number | undefined };

async function fetchPage(
  type: ActivityType | undefined,
  page: number | undefined,
  limit: number,
): Promise<ActivityPage> {
  const query = {
    limit,
    ...(type === undefined ? {} : { type }),
    ...(page === undefined ? {} : { page }),
  };
  return expectStatus(await api.activity.list({ query }), 200);
}

/**
 * One numbered page of the log, newest first. The previous page stays on
 * screen while the next loads, so paging does not flash a skeleton.
 */
export function useActivityPage(view: ActivityView): UseQueryResult<ActivityPage> {
  return useQuery({
    queryKey: ['activity', 'page', view.type ?? 'all', view.page],
    queryFn: () => fetchPage(view.type, view.page, PAGE_SIZE),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

/** The overview's "Live activity": the latest few rows of every type. */
export function useLatestActivity(count: number): UseQueryResult<WireActivityItem[]> {
  return useQuery({
    queryKey: ['activity', 'latest', count],
    queryFn: async () => (await fetchPage(undefined, undefined, count)).items,
    refetchInterval: 15_000,
  });
}
